import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import { ActionEditor } from '../components/ActionEditor'
import { PlanTaskEditor } from '../components/PlanTaskEditor'
import { Icon } from '../components/Icon'
import type {
  MyActionsPayload, EventAction, PlanTask, EventActionPatch, PlanTaskPatch,
  ActionPriority, PlanPriority
} from '../types'

// MyActions — cross-event aggregator of items needing the current user's
// attention. Restyled to match the v5 prototype's MyActionsView (line
// 2764 in docs/nymr_events_manager_5.html):
//
//   - Combined / Actions / Tasks tab strip with badge counts.
//   - Priority / event / status filters.
//   - Table layout (one row per item) with overdue rows highlighted.
//   - Click a row → ActionEditor or PlanTaskEditor opens as a popup.
//     Save invalidates ['my-actions'] so the table refreshes inline
//     — no navigation away from this page.
//
// Checklist items / risks / briefing acks were on the previous version
// of this page but aren't part of the prototype's surface — moved to a
// secondary "Other items waiting on you" panel below the table so they
// still surface without crowding the primary actions/tasks view.

type TabKey = 'combined' | 'actions' | 'tasks'
type StatusFilter = 'open' | 'all' | 'done'
type PriorityFilter = 'all' | 'high' | 'medium' | 'low'

type RowAction = MyActionsPayload['actions'][number]
type RowTask = MyActionsPayload['tasks'][number]

export function MyActions() {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-actions'],
    queryFn: ApiClient.myActions,
    refetchInterval: 60_000
  })

  const [tab, setTab] = useState<TabKey>('combined')
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all')
  const [eventFilter, setEventFilter] = useState<string>('all')   // 'all' | event.id as string
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open')

  // Editor state: which row (if any) is open. EventAction / PlanTask
  // shapes constructed from the my-actions payload — see toEventAction
  // and toPlanTask at the bottom. The payload carries the full editable
  // fields so no extra fetch is needed when the modal opens.
  const [editing, setEditing] = useState<
    | { kind: 'action'; row: RowAction }
    | { kind: 'task'; row: RowTask }
    | null
  >(null)

  // Mutations — share the existing ApiClient methods (used by ActionsTab
  // and PlanTab). Invalidate ['my-actions'] on top of the per-event
  // keys so this table refreshes immediately after a save.
  const patchAction = useMutation({
    mutationFn: ({ eventId, id, body }: { eventId: number; id: number; body: EventActionPatch }) =>
      ApiClient.patchAction(eventId, id, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['my-actions'] })
      qc.invalidateQueries({ queryKey: ['actions', vars.eventId] })
    }
  })
  const patchTask = useMutation({
    mutationFn: ({ eventId, id, body }: { eventId: number; id: number; body: PlanTaskPatch }) =>
      ApiClient.patchPlanTask(eventId, id, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['my-actions'] })
      qc.invalidateQueries({ queryKey: ['plan-tasks', vars.eventId] })
    }
  })

  // Distinct events across actions + tasks → drives the event filter
  // dropdown. Stable order: alphabetical by event name.
  const eventOptions = useMemo(() => {
    if (!data) return []
    const map = new Map<number, string>()
    for (const a of data.actions) map.set(a.event.id, a.event.name)
    for (const t of data.tasks)   map.set(t.event.id, t.event.name)
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  // Filter + sort actions: overdue first, then priority, then due date.
  const filteredActions = useMemo<RowAction[]>(() => {
    if (!data) return []
    let rows = data.actions
    if (priorityFilter !== 'all') rows = rows.filter((a) => a.priority === priorityFilter)
    if (eventFilter !== 'all') rows = rows.filter((a) => String(a.event.id) === eventFilter)
    if (statusFilter === 'done') {
      // backend already filters terminal statuses out; "Done" is
      // reserved for a future "show completed" path. Honest empty
      // state is right here, not silently showing irrelevant rows.
      rows = []
    }
    const today = todayIso()
    const PRI: Record<ActionPriority, number> = { high: 0, medium: 1, low: 2 }
    return [...rows].sort((a, b) => {
      const aOverdue = a.dueDate != null && a.dueDate < today
      const bOverdue = b.dueDate != null && b.dueDate < today
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1
      const p = PRI[a.priority] - PRI[b.priority]
      if (p !== 0) return p
      return (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999')
    })
  }, [data, priorityFilter, eventFilter, statusFilter])

  // Filter + sort tasks: overdue first, then end date.
  const filteredTasks = useMemo<RowTask[]>(() => {
    if (!data) return []
    let rows = data.tasks
    if (eventFilter !== 'all') rows = rows.filter((t) => String(t.event.id) === eventFilter)
    if (statusFilter === 'done') {
      rows = []  // backend filters progress<100 only; "done" is out of scope here
    }
    const today = todayIso()
    return [...rows].sort((a, b) => {
      const aOverdue = a.endAt < today
      const bOverdue = b.endAt < today
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1
      return a.endAt.localeCompare(b.endAt)
    })
  }, [data, eventFilter, statusFilter])

  const counts = {
    actions: filteredActions.length,
    tasks: filteredTasks.length,
    combined: filteredActions.length + filteredTasks.length
  }

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        Could not load your actions: {(error as Error).message}
      </div>
    )
  }
  if (!data) return null

  const otherCount =
    data.checklistItems.length + data.risks.length + data.briefings.length

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div>
        <h1 className="text-3xl mb-1">My actions &amp; tasks</h1>
        <p className="text-ink-soft">
          Everything assigned to you across all events — actions and project
          plan tasks together. Overdue first, then by date. Click a row to edit.
        </p>
      </div>

      {/* Tab strip — Combined / Actions / Tasks with counts */}
      <div className="flex flex-wrap items-center gap-2" role="tablist">
        <TabChip
          active={tab === 'combined'}
          onClick={() => setTab('combined')}
          icon="list"
          label="Combined"
          count={counts.combined}
        />
        <TabChip
          active={tab === 'actions'}
          onClick={() => setTab('actions')}
          icon="flag"
          label="Actions"
          count={counts.actions}
        />
        <TabChip
          active={tab === 'tasks'}
          onClick={() => setTab('tasks')}
          icon="bar-chart"
          label="Tasks"
          count={counts.tasks}
        />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-line rounded px-3 py-2">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft">Filter</span>
        {tab !== 'tasks' && (
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
            className="border border-line rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-green"
          >
            <option value="all">All priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        )}
        <select
          value={eventFilter}
          onChange={(e) => setEventFilter(e.target.value)}
          className="border border-line rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-green"
        >
          <option value="all">All events</option>
          {eventOptions.map((o) => (
            <option key={o.id} value={String(o.id)}>{o.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="border border-line rounded px-2 py-1 text-xs bg-white focus:outline-none focus:border-green"
          title="The /my-actions endpoint already filters to open + in-progress for actions, and progress<100 for tasks. The 'Done' option is reserved for a future 'show completed' surface."
        >
          <option value="open">Open + overdue</option>
          <option value="all">All statuses</option>
          <option value="done">Done</option>
        </select>
        <div className="ml-auto text-xs text-ink-soft font-mono">
          {tab === 'combined' && `${counts.combined} item${counts.combined === 1 ? '' : 's'}`}
          {tab === 'actions' && `${counts.actions} action${counts.actions === 1 ? '' : 's'}`}
          {tab === 'tasks' && `${counts.tasks} task${counts.tasks === 1 ? '' : 's'}`}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-line rounded overflow-hidden">
        {tab === 'combined' && (
          <CombinedTable
            actions={filteredActions}
            tasks={filteredTasks}
            onEditAction={(row) => setEditing({ kind: 'action', row })}
            onEditTask={(row) => setEditing({ kind: 'task', row })}
          />
        )}
        {tab === 'actions' && (
          <ActionsTable
            actions={filteredActions}
            onEdit={(row) => setEditing({ kind: 'action', row })}
          />
        )}
        {tab === 'tasks' && (
          <TasksTable
            tasks={filteredTasks}
            onEdit={(row) => setEditing({ kind: 'task', row })}
          />
        )}
      </div>

      {/* Secondary panel: items the prototype's MyActionsView doesn't
          surface but the previous version did. Keep them aggregated so
          managers see all to-dos in one place without crowding the
          primary table. */}
      {otherCount > 0 && (
        <OtherItemsPanel
          checklistItems={data.checklistItems}
          risks={data.risks}
          briefings={data.briefings}
        />
      )}

      {/* Edit popups */}
      {editing?.kind === 'action' && (
        <ActionEditor
          mode="edit"
          initial={toEventAction(editing.row)}
          saving={patchAction.isPending}
          serverError={patchAction.error as Error | null}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            patchAction.mutate(
              { eventId: editing.row.event.id, id: editing.row.id, body },
              { onSuccess: () => setEditing(null) }
            )
          }}
        />
      )}
      {editing?.kind === 'task' && (
        <PlanTaskEditor
          mode="edit"
          initial={toPlanTask(editing.row)}
          saving={patchTask.isPending}
          serverError={patchTask.error as Error | null}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            patchTask.mutate(
              { eventId: editing.row.event.id, id: editing.row.id, body },
              { onSuccess: () => setEditing(null) }
            )
          }}
        />
      )}
    </div>
  )
}

// ---- Tab chip ------------------------------------------------------------

function TabChip({
  active, onClick, icon, label, count
}: {
  active: boolean
  onClick: () => void
  icon: Parameters<typeof Icon>[0]['name']
  label: string
  count: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded border transition-colors ' +
        (active
          ? 'bg-green text-parchment border-green'
          : 'bg-white text-ink-soft border-line hover:bg-parchment hover:text-ink')
      }
    >
      <Icon name={icon} size={14} />
      {label}
      <span
        className={
          'ml-1 text-[10px] font-mono px-1.5 py-0.5 rounded ' +
          (active ? 'bg-white/20 text-parchment' : 'bg-parchment text-ink-soft')
        }
      >
        {count}
      </span>
    </button>
  )
}

// ---- Tables --------------------------------------------------------------

function CombinedTable({
  actions, tasks, onEditAction, onEditTask
}: {
  actions: RowAction[]
  tasks: RowTask[]
  onEditAction: (a: RowAction) => void
  onEditTask: (t: RowTask) => void
}) {
  type Combined =
    | { kind: 'action'; row: RowAction }
    | { kind: 'task'; row: RowTask }
  const today = todayIso()
  const all: Combined[] = [
    ...actions.map((row) => ({ kind: 'action' as const, row })),
    ...tasks.map((row) => ({ kind: 'task' as const, row }))
  ]
  all.sort((a, b) => {
    const aDate = a.kind === 'action' ? a.row.dueDate : a.row.endAt
    const bDate = b.kind === 'action' ? b.row.dueDate : b.row.endAt
    const aOverdue = aDate != null && aDate < today
    const bOverdue = bDate != null && bDate < today
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1
    return (aDate ?? '9999').localeCompare(bDate ?? '9999')
  })

  if (all.length === 0) return <EmptyState>Nothing to do — nice work!</EmptyState>

  return (
    <table className="w-full text-sm">
      <Thead>
        <th className="px-3 py-2 w-20">Type</th>
        <th className="px-3 py-2">Title</th>
        <th className="px-3 py-2 w-48">Event</th>
        <th className="px-3 py-2 w-32">Date</th>
        <th className="px-3 py-2 w-32">Priority / Progress</th>
      </Thead>
      <tbody>
        {all.map((item) => item.kind === 'action'
          ? <ActionRow key={`a-${item.row.id}`} row={item.row} combined onEdit={() => onEditAction(item.row)} />
          : <TaskRow   key={`t-${item.row.id}`} row={item.row} combined onEdit={() => onEditTask(item.row)} />
        )}
      </tbody>
    </table>
  )
}

function ActionsTable({ actions, onEdit }: { actions: RowAction[]; onEdit: (a: RowAction) => void }) {
  if (actions.length === 0) return <EmptyState>No actions match these filters.</EmptyState>
  return (
    <table className="w-full text-sm">
      <Thead>
        <th className="px-3 py-2">Action</th>
        <th className="px-3 py-2 w-48">Event</th>
        <th className="px-3 py-2 w-32">Department</th>
        <th className="px-3 py-2 w-32">Due</th>
        <th className="px-3 py-2 w-24">Priority</th>
      </Thead>
      <tbody>
        {actions.map((row) => (
          <ActionRow key={row.id} row={row} onEdit={() => onEdit(row)} />
        ))}
      </tbody>
    </table>
  )
}

function TasksTable({ tasks, onEdit }: { tasks: RowTask[]; onEdit: (t: RowTask) => void }) {
  if (tasks.length === 0) return <EmptyState>No tasks match these filters.</EmptyState>
  return (
    <table className="w-full text-sm">
      <Thead>
        <th className="px-3 py-2">Task</th>
        <th className="px-3 py-2 w-48">Event</th>
        <th className="px-3 py-2 w-32">Group</th>
        <th className="px-3 py-2 w-44">Schedule</th>
        <th className="px-3 py-2 w-32">Progress</th>
      </Thead>
      <tbody>
        {tasks.map((row) => (
          <TaskRow key={row.id} row={row} onEdit={() => onEdit(row)} />
        ))}
      </tbody>
    </table>
  )
}

function Thead({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line bg-parchment/60">
        {children}
      </tr>
    </thead>
  )
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-12 text-center text-sm text-ink-soft">
      {children}
    </div>
  )
}

// ---- Row renderers -------------------------------------------------------
//
// The two row renderers handle both the per-tab tables (Actions / Tasks
// only — `combined` prop omitted, the type column isn't rendered) and the
// Combined tab (where the type column comes first and the second-rank
// metadata moves into the title cell's subtitle).

function ActionRow({ row, combined, onEdit }: { row: RowAction; combined?: boolean; onEdit: () => void }) {
  const today = todayIso()
  const overdue = row.dueDate != null && row.dueDate < today
  return (
    <tr
      onClick={onEdit}
      className="border-b border-line last:border-b-0 cursor-pointer transition-colors hover:bg-parchment/40"
      style={overdue ? { background: '#f8dbdd33' } : undefined}
    >
      {combined && (
        <td className="px-3 py-3">
          <TypeBadge kind="action" />
        </td>
      )}
      <td className="px-3 py-3">
        <div className="font-semibold">{row.title}</div>
        {combined && row.department && (
          <div className="text-xs text-ink-soft mt-0.5">{row.department}</div>
        )}
      </td>
      <td className="px-3 py-3 text-sm text-ink-soft">{row.event.name}</td>
      {!combined && (
        <td className="px-3 py-3 text-xs text-ink-soft">{row.department ?? '—'}</td>
      )}
      <td className="px-3 py-3 text-xs font-mono">
        {row.dueDate
          ? <span className={overdue ? 'text-oxblood font-semibold' : 'text-ink-soft'}>
              {formatDateShort(row.dueDate)}{overdue && ' ⚠'}
            </span>
          : <span className="text-ink-soft italic">—</span>}
      </td>
      <td className="px-3 py-3">
        <PriorityBadge priority={row.priority} />
      </td>
    </tr>
  )
}

function TaskRow({ row, combined, onEdit }: { row: RowTask; combined?: boolean; onEdit: () => void }) {
  const today = todayIso()
  const overdue = row.endAt < today && row.progress < 100
  return (
    <tr
      onClick={onEdit}
      className="border-b border-line last:border-b-0 cursor-pointer transition-colors hover:bg-parchment/40"
      style={overdue ? { background: '#f8dbdd33' } : undefined}
    >
      {combined && (
        <td className="px-3 py-3">
          <TypeBadge kind="task" />
        </td>
      )}
      <td className="px-3 py-3">
        <div className="font-semibold">{row.name}</div>
        {combined && <div className="text-xs text-ink-soft mt-0.5">{row.group}</div>}
      </td>
      <td className="px-3 py-3 text-sm text-ink-soft">{row.event.name}</td>
      {!combined && (
        <td className="px-3 py-3 text-xs text-ink-soft">{row.group}</td>
      )}
      <td className="px-3 py-3 text-xs font-mono">
        <span className={overdue ? 'text-oxblood font-semibold' : 'text-ink-soft'}>
          {formatDateShort(row.startAt)} – {formatDateShort(row.endAt)}{overdue && ' ⚠'}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <div className="h-1.5 rounded bg-parchment flex-1 overflow-hidden" style={{ minWidth: 60 }}>
            <div
              className="h-full bg-green transition-all"
              style={{ width: `${row.progress}%` }}
            />
          </div>
          <span className="text-xs font-mono font-semibold text-ink-soft">{row.progress}%</span>
        </div>
      </td>
    </tr>
  )
}

// ---- Badges --------------------------------------------------------------

function TypeBadge({ kind }: { kind: 'action' | 'task' }) {
  const style = kind === 'action'
    ? { background: '#f8dbdd', color: '#7a2329' }
    : { background: '#e0e7ec', color: '#3d5a6c' }
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={style}
    >
      <Icon name={kind === 'action' ? 'flag' : 'bar-chart'} size={10} />
      {kind}
    </span>
  )
}

const PRIORITY_STYLE: Record<ActionPriority | PlanPriority, { bg: string; fg: string }> = {
  low:    { bg: '#e5eff0', fg: '#3d5a6c' },
  medium: { bg: '#f5edd5', fg: '#9b7f2a' },
  high:   { bg: '#f8dbdd', fg: '#7a2329' }
}

function PriorityBadge({ priority }: { priority: ActionPriority | PlanPriority }) {
  const s = PRIORITY_STYLE[priority]
  return (
    <span
      className="inline-flex text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
    >
      {priority}
    </span>
  )
}

// ---- "Other items" panel -------------------------------------------------

function OtherItemsPanel({
  checklistItems, risks, briefings
}: {
  checklistItems: MyActionsPayload['checklistItems']
  risks: MyActionsPayload['risks']
  briefings: MyActionsPayload['briefings']
}) {
  return (
    <div className="bg-white border border-line rounded p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft">
        Other items waiting on you
      </div>
      <div className="text-xs text-ink-soft space-y-1.5">
        {checklistItems.length > 0 && (
          <div>
            <span className="font-semibold text-ink">
              {checklistItems.length} checklist item{checklistItems.length === 1 ? '' : 's'}
            </span>{' '}
            across {new Set(checklistItems.map((i) => i.eventId)).size} event
            {new Set(checklistItems.map((i) => i.eventId)).size === 1 ? '' : 's'}
          </div>
        )}
        {risks.length > 0 && (
          <div>
            <span className="font-semibold text-ink">
              {risks.length} risk{risks.length === 1 ? '' : 's'}
            </span> awaiting sign-off
          </div>
        )}
        {briefings.length > 0 && (
          <div>
            <span className="font-semibold text-ink">
              {briefings.length} briefing{briefings.length === 1 ? '' : 's'}
            </span> to acknowledge
            {briefings.some((b) => b.isStale) && (
              <span className="text-brass"> · some have updated versions</span>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-ink-soft italic">
        Edit these from the relevant event's tab — checklist items / risks /
        briefings have their own per-event surfaces.
      </div>
    </div>
  )
}

// ---- Editor adapters -----------------------------------------------------
//
// The /my-actions payload carries the full editable shape of each row;
// constructing an EventAction or PlanTask is a 1-to-1 mapping plus a few
// derived fields (assigneeName, createdAt etc.) that the editor doesn't
// read but the type requires.

function toEventAction(row: RowAction): EventAction {
  return {
    id: row.id,
    eventId: row.event.id,
    title: row.title,
    description: row.description,
    department: row.department,
    assigneeId: row.assigneeId,
    assigneeName: null,
    assignees: row.assigneeId != null ? [{ id: row.assigneeId, name: null, avatarInitials: null }] : [],
    assigneeIds: row.assigneeId != null ? [row.assigneeId] : [],
    planTaskId: null,
    onPlan: false,
    dueAt: row.dueDate,
    priority: row.priority,
    status: row.status,
    completedAt: null,
    completedByName: null,
    createdAt: '',
    createdByName: null
  }
}

function toPlanTask(row: RowTask): PlanTask {
  return {
    id: row.id,
    eventId: row.event.id,
    name: row.name,
    description: row.description,
    group: row.group,
    assigneeId: row.assigneeId,
    assigneeName: null,
    startAt: row.startAt,
    endAt: row.endAt,
    progress: row.progress,
    priority: row.priority,
    notes: row.notes
  }
}

// ---- Helpers -------------------------------------------------------------

function todayIso(): string {
  const d = new Date()
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function formatDateShort(iso: string): string {
  // "2026-05-08" → "8 May"
  try {
    const d = new Date(`${iso}T00:00:00Z`)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  } catch {
    return iso
  }
}
